import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { _className, ComponentSync, Grid, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import './Renderer_FishBone.scss';
import { debounce, exists } from '@nu-art/ts-common';
import { resolveValueDisplay, valueID_Pos, valueType_Enumerated, valueType_Range, valueType_StandardRange, valueType_Tuple } from '@app/hcs-shared';
import { Tooltip_Finding_Enum, Tooltip_Finding_Numeric } from '../Tooltip_Finding/index.js';
import { translateFishboneResultsToTooltipData } from './utils/translate-to-tooltip.js';
export class Renderer_FishBone extends ComponentSync {
    //######################### Class Properties #########################
    renderRef = React.createRef();
    reCalcShortAxis = debounce(() => this.setShortAxisLength(), 300);
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.struct = nextProps.struct;
        state.results = nextProps.results;
        state.shortAxisLength ??= 0;
        state.showTooltipSource = nextProps.showTooltipSource;
        return state;
    }
    componentDidMount() {
        this.setShortAxisLength();
    }
    __onWindowResized() {
        this.reCalcShortAxis();
    }
    //######################### Logic #########################
    setShortAxisLength = () => {
        if (!this.renderRef.current)
            return;
        const rect = this.renderRef.current.getBoundingClientRect();
        const shortAxisLength = Math.min(rect.width, rect.height);
        this.setState({ shortAxisLength });
    };
    getStyle = () => {
        const ribs = this.state.struct.ribs;
        const maxCells = Math.max(...ribs.map(rib => rib.ribItems.length));
        return {
            '--cell-count': maxCells,
            '--rib-count': ribs.length,
            '--fishbone-length': `${this.state.shortAxisLength}px`,
        };
    };
    valueInRange = (result) => {
        if (!result?.value)
            return true;
        switch (typeof result.value) {
            case 'number': {
                const res = result;
                return res.value >= res.normalMin && res.value <= res.normalMax;
            }
            case 'string': {
                if (result.value === valueID_Pos)
                    return false;
            }
        }
        return true;
    };
    getLatestResultData = (ribItem) => {
        let variableId;
        let result;
        ribItem.variableIds?.forEach(id => {
            if (!variableId) {
                variableId = id;
                result = this.state.results[id]?.[0];
                return;
            }
            const latestResult = this.state.results[id]?.[0];
            if (!latestResult)
                return;
            if (latestResult.timestamp > (result?.timestamp ?? 0)) {
                variableId = id;
                result = latestResult;
            }
        });
        return {
            variableId,
            result
        };
    };
    resolveResultMetadata_Numeric = (variableId, result) => {
        const metadata = {
            isCurrentEncounter: result?.currentVisit,
            label: '-',
            tooltipData: translateFishboneResultsToTooltipData(this.props.modules, variableId, this.state.results[variableId]),
            valueInRange: this.valueInRange(result),
        };
        if (!exists(result?.value))
            return metadata;
        if (!result.currentVisit) {
            metadata.label = '...';
        }
        else {
            metadata.label = String(result.value);
        }
        return metadata;
    };
    resolveResultMetadata_Enum = (variableId, result) => {
        const metadata = {
            isCurrentEncounter: result?.currentVisit,
            label: '-',
            tooltipData: translateFishboneResultsToTooltipData(this.props.modules, variableId, this.state.results[variableId]),
            valueInRange: this.valueInRange(result),
        };
        if (!exists(result?.value))
            return metadata;
        if (!result.currentVisit) {
            metadata.label = '...';
        }
        else {
            const value = this.props.modules.value.cache.unique(result?.value);
            if (value)
                metadata.label = resolveValueDisplay(value).substring(0, 3);
        }
        return metadata;
    };
    //######################### Render #########################
    render() {
        const className = _className('renderer__fish-bone', this.state.struct.direction);
        return _jsxs("div", { className: className, style: this.getStyle(), ref: this.renderRef, children: [this.render_Head(), this.render_Ribs(), this.render_Tail()] });
    }
    render_RibCellShortName = (shortName) => {
        if (!shortName)
            return;
        const strLen = shortName.length;
        const nodes = [];
        for (let i = 0; i < strLen; i++) {
            const char = shortName[i];
            if (char === '_' && (i + 1) < strLen) {
                const nextChar = shortName[i + 1];
                nodes.push(_jsx("sub", { children: nextChar }));
                i++;
            }
            else if (char === '^' && (i + 1) < strLen) {
                const nextChar = shortName[i + 1];
                nodes.push(_jsx("sup", { children: nextChar }));
                i++;
            }
            else {
                nodes.push(char);
            }
        }
        return _jsxs("div", { className: 'renderer__fish-bone__short-name', children: [...nodes] });
    };
    //######################### Render - Head #########################
    render_Head = () => {
        const item = this.state.struct.head;
        if (!item)
            return;
        return _jsxs(LL_V_L, { className: 'renderer__fish-bone__head', children: [this.render_Value(item), this.render_RibCellShortName(item.shortName)] });
    };
    //######################### Render - Tail #########################
    render_Tail = () => {
        const item = this.state.struct.tail;
        if (!item)
            return;
        return _jsxs(LL_V_L, { className: 'renderer__fish-bone__tail', children: [this.render_Value(item), this.render_RibCellShortName(item.shortName)] });
    };
    //######################### Render - Ribs #########################
    render_Ribs = () => {
        const ribs = this.state.struct.ribs;
        return _jsx(Grid, { className: 'renderer__fish-bone__ribs', children: ribs.map((rib, index) => {
                const className = _className('renderer__fish-bone__rib', rib.slantDividers && 'slant-dividers');
                return _jsx(Grid, { className: className, children: rib.ribItems.map(this.render_RibCell) }, index);
            }) });
    };
    render_RibCell = (ribItem) => {
        return _jsxs(LL_V_L, { className: 'renderer__fish-bone__rib-cell', children: [this.render_Value(ribItem), this.render_RibCellShortName(ribItem.shortName)] }, ribItem.variableIds?.[0]);
    };
    //######################### Render - Value #########################
    render_Value = (ribItem) => {
        const resultData = this.getLatestResultData(ribItem);
        const variable = this.props.modules.variable.cache.unique(resultData.variableId);
        const value = this.props.modules.value.cache.unique(variable?.valueId);
        switch (value?.type) {
            case valueType_StandardRange:
            case valueType_Range:
                return this.render_Value_Numeric(resultData.variableId, resultData.result);
            case valueType_Tuple:
            case valueType_Enumerated:
                return this.render_Value_Enum(resultData.variableId, resultData.result);
            default: {
                this.logWarning(`Unsupported value type: ${value?.type}`, value);
                return _jsx("div", { className: 'renderer__fish-bone__value', children: "N/A" });
            }
        }
    };
    render_Value_Numeric = (variableId, latestResult) => {
        const metadata = this.resolveResultMetadata_Numeric(variableId, latestResult);
        const className = _className('renderer__fish-bone__value', (exists(metadata.valueInRange) && !metadata.valueInRange) && 'not-in-range', !metadata.isCurrentEncounter && 'fade');
        return _jsx(Tooltip_Finding_Numeric, { modules: this.props.modules, data: metadata.tooltipData, showSource: this.state.showTooltipSource, onTooltipOpened: () => this.props.onCellTooltipOpen?.(variableId, metadata), children: _jsx("div", { className: className, children: metadata.label }) });
    };
    render_Value_Enum = (variableId, latestResult) => {
        const metadata = this.resolveResultMetadata_Enum(variableId, latestResult);
        const className = _className('renderer__fish-bone__value', (exists(metadata.valueInRange) && !metadata.valueInRange) && 'not-in-range', !metadata.isCurrentEncounter && 'fade');
        return _jsx(Tooltip_Finding_Enum, { modules: this.props.modules, data: metadata.tooltipData, showSource: this.state.showTooltipSource, onTooltipOpened: () => this.props.onCellTooltipOpen?.(variableId, metadata), children: _jsx("div", { className: className, children: metadata.label }) });
    };
}
//# sourceMappingURL=Renderer_FishBone.js.map